import React from 'react';
import { Navigate } from 'react-router-dom';
import { useRBAC } from '../contexts/RBACContext';
import { TabPermissions } from '../types';

/**
 * ProtectedRoute
 *
 * Wraps a route element with permission checks. If the user does not have
 * permission for the required tab, redirects to the first permitted tab.
 *
 * Tab order for redirect: Logs (/) → Monitoring (/monitoring) → Configuration (/configuration) → System (/system)
 *
 * Requirements: 7.3, 7.4
 */

interface ProtectedRouteProps {
  requiredTab: keyof TabPermissions;
  children: React.ReactNode;
}

/**
 * Ordered list of tabs for determining the first permitted redirect target.
 * Order: Logs → Monitoring → Configuration → System
 */
const TAB_ORDER: { tab: keyof TabPermissions; path: string }[] = [
  { tab: 'logs', path: '/' },
  { tab: 'monitoring', path: '/monitoring' },
  { tab: 'configuration', path: '/configuration' },
  { tab: 'system', path: '/system' },
];

export function getFirstPermittedPath(permissions: TabPermissions | null): string {
  if (!permissions) {
    return '/';
  }
  for (const { tab, path } of TAB_ORDER) {
    if (permissions[tab]) {
      return path;
    }
  }
  // Fallback: if no tabs are permitted, go to root
  return '/';
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ requiredTab, children }) => {
  const { permissions, loading } = useRBAC();

  // While RBAC is loading, don't render anything yet
  if (loading) {
    return null;
  }

  // If permissions are loaded and user has access, render the children
  if (permissions && permissions[requiredTab]) {
    return <>{children}</>;
  }

  // User doesn't have permission — redirect to first permitted tab
  const redirectPath = getFirstPermittedPath(permissions);
  return <Navigate to={redirectPath} replace />;
};

export default ProtectedRoute;
